import type { MarketIdentifiers } from '$lib/types/market';
import { prepareWriteContract, writeContract, type SendTransactionResult, fetchFeeData } from '@wagmi/core';
import abi from '$lib/web3/abi';
import { ZERO_ADDRESS } from '$lib/web3/addresses';
import { buildMarginCallData } from '../builders/margin';
import type { UserMargin } from '$lib/types/user';
import { estimateGasForConnectors, getMaxFeeAndPriorityFee } from '../utils/gas';
import { buildExpiredOrderCallData } from '../builders/orders';
import type { Order } from '$lib/types/order';
import { estimateGasLimits } from '../estimateGaslimit';
import {
	buildCancelLimitTradeSmartContract,
	buildCloseFullLimitTradeSmartContract
} from '../builders/tradeLimit';
import {
	buildCancelAllOrdersCallData,
	buildCancelFullOrderCallData,
	buildFullOrderCallData
} from '../builders/advancedOrders';
import { updateKeeperFee } from '../connectors/fees';
import { isItBedRockShutDownTime, throwError } from '$lib/home/util';
import { buildSwapMatchaCalldata } from '../builders/swap';
import type { IMatchaResponse } from '../types';
import BigNumber from 'bignumber.js';
import type { Token, TokenSymbol } from '$lib/types/tokens';
import { ethers } from 'ethers';
import { MAX_PRIORITY_FEE_PER_GAS } from '../contants';

export async function closeFullLimitTradeSmartContract(
	marketIdentifier: MarketIdentifiers,
	priceImpact: string,
	isUpper: boolean,
	triggerPrice: string,
	limitPrice: string,
	expiry: string,
	smartContractWalletAddress: `0x${string}`,
	shouldUpdateKeeperFee: boolean
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const functionName: string = 'cast';
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	if (shouldUpdateKeeperFee) {
		const updateKeeperFeeCallData = updateKeeperFee();
		targetArray.push(updateKeeperFeeCallData._target);
		callDataArray.push(updateKeeperFeeCallData._callData);
	}

	const closeFullLimitTradeSmartContractCallData = await buildCloseFullLimitTradeSmartContract(
		marketIdentifier,
		priceImpact,
		isUpper,
		triggerPrice,
		limitPrice,
		expiry
	);

	targetArray.push(...closeFullLimitTradeSmartContractCallData._targetArray);
	callDataArray.push(...closeFullLimitTradeSmartContractCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);


	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();


	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});

	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function addMarginLimitTradeSmartContract(
	marginDelta: string,
	sizeDelta: string,
	limitPrice: string,
	triggerPrice: string,
	takeProfitInput: string,
	stopLossInput: string,
	expiry: string,
	marketIdentifier: MarketIdentifiers,
	priceImpact: string,
	marginsWithoutPositions: Array<UserMargin>,
	tokensAllowedForDepositBalance: Record<string, Token>,
	expiredOrders: Array<Order>,
	smartContractWalletAddress: `0x${string}`,
	AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION: boolean,
	shouldUpdateKeeperFee: boolean,
	amountPerTokenToSwap: Record<string, BigNumber>,
	matchaCallData: Record<string, IMatchaResponse>,
	AUTOSWAP_STABLES: boolean,
	AUTOSWAP_OP: boolean,
	tokensAllowedForDeposit: Array<TokenSymbol>
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const functionName: string = 'cast';
	const targetArray: Array<string> = [];
	const callData: Array<string> = [];

	const cancelExpiredOrdersCallData = await buildExpiredOrderCallData(expiredOrders);

	targetArray.push(...cancelExpiredOrdersCallData._targetArray);
	callData.push(...cancelExpiredOrdersCallData._callDataArray);

	const isAddMaxMargin = marginDelta === ethers.constants.MaxUint256.toString();
	if (AUTOSWAP_STABLES || AUTOSWAP_OP) {
		const swapCallData = await buildSwapMatchaCalldata(
			tokensAllowedForDeposit,
			tokensAllowedForDepositBalance,
			amountPerTokenToSwap,
			matchaCallData,
			marketIdentifier,
			isAddMaxMargin
		);
		targetArray.push(...swapCallData._targetArray);
		callData.push(...swapCallData._callDataArray);

		if (swapCallData._changedMargin && new BigNumber(marginDelta).gt(0) && !isAddMaxMargin) {
			if (new BigNumber(marginDelta).minus(swapCallData._changedMargin as BigNumber).gt(0)) {
				marginDelta = new BigNumber(marginDelta)
					.minus(swapCallData._changedMargin as BigNumber)
					.toFixed(0);
			} else {
				marginDelta = '0';
			}
		}
	}

	const marginCallData = buildMarginCallData(
		marginsWithoutPositions,
		marketIdentifier,
		marginDelta,
		AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION
	);

	targetArray.push(...marginCallData._targetArray);
	callData.push(...marginCallData._callData);

	if (shouldUpdateKeeperFee) {
		const updateKeeperFeeCallData = updateKeeperFee();
		targetArray.push(updateKeeperFeeCallData._target);
		callData.push(updateKeeperFeeCallData._callData);
	}

	const fullOrderCallData = buildFullOrderCallData(
		marketIdentifier,
		sizeDelta,
		priceImpact,
		expiry,
		limitPrice,
		triggerPrice,
		takeProfitInput,
		stopLossInput
	);

	targetArray.push(...fullOrderCallData._targetArray);
	callData.push(...fullOrderCallData._callData);

	let gasLimit = await estimateGasForConnectors(
		_smartContractWalletAddress,
		targetArray,
		callData,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();


	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callData, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function cancelLimitTradeSmartContract(
	orderId: string,
	smartContractWalletAddress: `0x${string}`
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const functionName: string = 'cast';
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	const cancelLimitTradeSmartContractCallData = await buildCancelLimitTradeSmartContract(orderId);

	targetArray.push(...cancelLimitTradeSmartContractCallData._targetArray);
	callDataArray.push(...cancelLimitTradeSmartContractCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();


	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function cancelFullOrderSmartContract(
	orderId: string,
	smartContractWalletAddress: `0x${string}`
) {
	const _smartContractWalletAddress = smartContractWalletAddress;
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];
	const functionName: string = 'cast';

	const cancelFullOrderSmartContractCallData = await buildCancelFullOrderCallData(orderId);

	targetArray.push(...cancelFullOrderSmartContractCallData._targetArray);
	callDataArray.push(...cancelFullOrderSmartContractCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();


	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});

	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function cancelAllOrdersSmartContract(
	orderIds: Array<string>,
	smartContractWalletAddress: string
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const functionName: string = 'cast';
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	const cancelAllOrdersSmartContractCallData = await buildCancelAllOrdersCallData(orderIds);

	targetArray.push(...cancelAllOrdersSmartContractCallData._targetArray);
	callDataArray.push(...cancelAllOrdersSmartContractCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();


	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}
